import React from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  Paper,
  alpha,
  useTheme,
  Container,
  Fade,
} from "@mui/material";
import { usePOSService } from "../../hooks/usePOSService";
import { SessionStart } from "../SessionManagement/SessionInitialization/SessionStart";

interface SessionCheckProps {
  children: React.ReactNode;
}

export const SessionCheck: React.FC<SessionCheckProps> = ({ children }) => {
  const theme = useTheme();
  const { isSessionActive, startSession } = usePOSService();
  const [sessionStartOpen, setSessionStartOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // Custom colors to match the screenshot
  const colors = {
    primary: '#1B7B6B',  // Deep teal from header
    success: '#4CAF50',  // Green for completed status
    background: '#FFFFFF',
    text: {
      primary: '#2C3E50',
      secondary: '#546E7A',
    }
  };

  const handleSessionStart = async (sessionData: {
    initial_amount: number;
    denominations: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  }) => {
    try {
      setIsLoading(true);
      await startSession(sessionData);
      setSessionStartOpen(false);
    } catch (error) {
      console.error("Error starting session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSessionActive) {
    return (
      <Container maxWidth="sm" sx={{ height: "100%" }}>
        <Fade in timeout={800}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              p: { xs: 2, sm: 3 },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                width: "100%",
                textAlign: "center",
                backgroundColor: colors.background,
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                border: `1px solid ${alpha(colors.primary, 0.1)}`,
                boxShadow: `0 8px 32px ${alpha(colors.text.primary, 0.08)}`,
                transition: theme.transitions.create(['transform', 'box-shadow']),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 40px ${alpha(colors.text.primary, 0.12)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: alpha(colors.primary, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <Icon
                  sx={{
                    fontSize: 40,
                    color: colors.primary,
                  }}
                >
                  point_of_sale
                </Icon>
              </Box>
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: colors.text.primary,
                }}
              >
                No Active Session
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 300,
                  mx: 'auto',
                  lineHeight: 1.6,
                  color: colors.text.secondary,
                }}
              >
                You need an active session to access this feature
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Icon>add</Icon>}
                onClick={() => setSessionStartOpen(true)}
                sx={{
                  mt: 2,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  backgroundColor: colors.primary,
                  '&:hover': {
                    backgroundColor: alpha(colors.primary, 0.9),
                    boxShadow: `0 6px 16px ${alpha(colors.primary, 0.4)}`,
                  },
                }}
              >
                Start New Session
              </Button>
            </Paper>

            <SessionStart
              open={sessionStartOpen}
              onClose={() => setSessionStartOpen(false)}
              onSessionStart={handleSessionStart}
              isLoading={isLoading}
            />
          </Box>
        </Fade>
      </Container>
    );
  }

  return <>{children}</>;
};
